/**
 * 分身出片配乐面板(2026-09-07)—— 照抄视频线 `VideoBgmPanel` 的形制(平台预置 Select+试听
 * / 自定义上传 mp3 二选一,「不配乐」为默认),但**不直接 import 它**:那个组件耦合视频线
 * 自己的 `FormState`,分身页的表单是散落的独立 `useState`,硬套会逼分身页拼一个假 FormState。
 * 状态落在这里定义的窄 props 上,由分身创建页自己的 bgmId/customBgmObjectKey state 承接。
 *
 * 只在**出视频**的档位(真人口播 / 照片生动 / 照片省流)挂载 —— 纯配音档(tts)产物是音频,
 * 不吃 BGM,页面侧按 `!isTts` 整块不渲染,不是禁用。
 *
 * ★曲库类型/service 走 `@/entities/media-bgm`,不是 `@/features/video`:features 之间禁止
 *   互相 import(ADR-0013 单向核心,eslint no-restricted-imports 会拦),跨 feature 共享
 *   业务能力要下沉 entities —— 与 `entities/dh-profile` 当年从 digital-human 抽出同一条规矩。
 */
import { useRef } from "react";
import { Upload, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Slider } from "@/shared/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { pauseOtherAudio } from "@/shared/lib/exclusiveAudio";
import type { BgmDto } from "@/entities/media-bgm/api/bgmService";

export type DhBgmTab = "preset" | "custom";

export interface DhBgmPanelProps {
  bgms: BgmDto[];
  bgmTab: DhBgmTab;
  onBgmTabChange: (v: DhBgmTab) => void;
  /** 平台曲库当前选中曲目 id;null = 未选(不配乐或走自定义)。 */
  bgmId: number | null;
  /** 选平台曲目(含选「无 BGM」→ null)。调用方须同时清掉自定义上传(与视频线同规矩)。 */
  onSelectPresetBgm: (id: number | null) => void;
  /** 自定义上传拿到的 MinIO objectKey;null = 未上传或已清。 */
  customBgmObjectKey: string | null;
  customBgmFile: File | null;
  customBgmUrl: string | null;
  uploadingBgm: boolean;
  onClearCustomBgm: () => void;
  onCustomBgmUpload: (f: File) => void;
  /** BGM 基线音量(0.1~0.8;2026-09-08 老板「配乐声太小」+ 加调音量按钮)。 */
  bgmVolume: number;
  onBgmVolumeChange: (v: number) => void;
}

// 音量滑杆范围 = ai 侧合法域(bgm.py `_BGM_VOLUME_MIN/MAX`);默认 0.45(ai 同默认,已比旧 0.30 响)。
const BGM_VOLUME_MIN = 0.1;
const BGM_VOLUME_MAX = 0.8;

export function DhBgmPanel({
  bgms,
  bgmTab,
  onBgmTabChange,
  bgmId,
  onSelectPresetBgm,
  customBgmObjectKey,
  customBgmFile,
  customBgmUrl,
  uploadingBgm,
  onClearCustomBgm,
  onCustomBgmUpload,
  bgmVolume,
  onBgmVolumeChange,
}: DhBgmPanelProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const hasBgm = bgmId != null || customBgmObjectKey != null;
  return (
    <div className="space-y-1.5" data-testid="dh-bgm-panel">
      <Label className="text-xs">背景音乐(可选,不选则不配乐)</Label>
      <Tabs value={bgmTab} onValueChange={(v) => onBgmTabChange(v as DhBgmTab)}>
        <TabsList className="grid grid-cols-2 w-full h-8">
          <TabsTrigger value="preset" className="text-xs">
            平台预置 ({bgms.length})
          </TabsTrigger>
          <TabsTrigger value="custom" className="text-xs">
            自定义上传
          </TabsTrigger>
        </TabsList>

        <TabsContent value="preset" className="space-y-2 mt-2">
          <Select
            value={bgmId ? String(bgmId) : "none"}
            onValueChange={(v) => {
              // 与视频线 VideoBgmPanel 同规矩:走这个 Select 一律先清自定义(哪怕选的是
              // 「无 BGM」)—— 否则「上传过自定义又切回平台选择」时旧的自定义 key 会残留。
              onClearCustomBgm();
              onSelectPresetBgm(v === "none" ? null : Number(v));
            }}
          >
            <SelectTrigger className="h-8 text-xs" aria-label="平台预置 BGM">
              <SelectValue placeholder="选择 BGM" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">不配乐</SelectItem>
              {bgms.map((b) => (
                <SelectItem key={b.id} value={String(b.id)}>
                  {b.name} · {b.durationSec}s
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {bgmId && bgms.find((b) => b.id === bgmId)?.url && (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              onPlay={pauseOtherAudio}
              src={bgms.find((b) => b.id === bgmId)!.url}
              controls
              preload="none"
              className="h-8 w-full"
              aria-label="试听 BGM"
            />
          )}
        </TabsContent>

        <TabsContent value="custom" className="mt-2">
          <input
            ref={fileRef}
            type="file"
            accept="audio/mpeg,audio/mp3"
            className="hidden"
            disabled={uploadingBgm}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onCustomBgmUpload(f);
              e.target.value = "";
            }}
          />
          {!customBgmObjectKey ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0 gap-1.5"
                disabled={uploadingBgm}
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="h-3.5 w-3.5" />
                {uploadingBgm ? "上传中…" : "上传 mp3"}
              </Button>
              <span className="text-2xs text-muted-foreground">mp3 ≤10MB · 自行确保版权合规</span>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 rounded-md border bg-background/40 px-2.5 py-1.5">
                <span className="min-w-0 flex-1 truncate text-xs" title={customBgmFile?.name}>
                  {customBgmFile?.name ?? "自传 mp3"}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                  aria-label="移除自传 BGM"
                  onClick={onClearCustomBgm}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              {customBgmUrl && (
                // eslint-disable-next-line jsx-a11y/media-has-caption
                <audio
                  onPlay={pauseOtherAudio}
                  src={customBgmUrl}
                  controls
                  preload="none"
                  className="h-8 w-full"
                  aria-label="试听自传 BGM"
                />
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* 音量:只在真选了 BGM 时才出现(没配乐调音量是空操作)。滑杆值即 ai 侧基线电平。 */}
      {hasBgm && (
        <div className="space-y-1 pt-1" data-testid="dh-bgm-volume">
          <div className="flex items-center justify-between">
            <Label className="text-xs">配乐音量</Label>
            <span className="text-xs tabular-nums text-muted-foreground">
              {Math.round(bgmVolume * 100)}%
            </span>
          </div>
          <Slider
            aria-label="配乐音量"
            aria-valuetext={`${Math.round(bgmVolume * 100)}%`}
            min={BGM_VOLUME_MIN}
            max={BGM_VOLUME_MAX}
            step={0.05}
            value={[bgmVolume]}
            onValueChange={([v]) => onBgmVolumeChange(Math.round(v * 20) / 20)}
          />
          <p className="text-2xs text-muted-foreground">
            口播时会自动压低配乐让人声清晰;这里调的是间奏时的响度。
          </p>
        </div>
      )}
    </div>
  );
}
